import { useState, useEffect, useRef } from 'react';
import { useHealthKit } from '@/hooks/useHealthKit';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface HealthConnectionPromptProps {
  isOpen: boolean;
  onClose: () => void;
  userId?: string;
  trigger: 'onboarding' | 'first_workout';
}

/**
 * Health Connection Prompt - Native Only
 * 
 * No custom UI. When this component becomes active, it immediately
 * triggers the native iOS HealthKit system permission dialog.
 * This complies with Apple guidelines by showing ONLY the native prompt.
 */
export function HealthConnectionPrompt({ 
  isOpen, 
  onClose, 
  userId,
  trigger 
}: HealthConnectionPromptProps) {
  const { 
    status, 
    isNativeApp, 
    platformName,
    requestAuthorization,
    clearDeniedState,
  } = useHealthKit();
  
  const [dismissed, setDismissed] = useState(false);
  const hasTriggered = useRef(false);

  // Check if already prompted for this trigger
  useEffect(() => {
    const alreadyPrompted = localStorage.getItem(`health_prompted_${trigger}`);
    if (alreadyPrompted === 'true') {
      setDismissed(true);
    }
  }, [trigger]);

  const shouldShow = isOpen && isNativeApp && !status.isAuthorized && !dismissed;

  // Immediately trigger native permission when this becomes active
  useEffect(() => {
    if (!shouldShow || hasTriggered.current) return;
    hasTriggered.current = true;

    console.log('[HealthPrompt] Triggering native permission dialog directly');
    
    // Clear previous state
    clearDeniedState();
    localStorage.removeItem('health_authorization_denied');
    localStorage.removeItem('health_authorized');
    localStorage.removeItem('health_explicit_grant');

    // Trigger native permission immediately
    requestAuthorization().then(async (result) => {
      if (result.success) {
        toast.success(`Connected to ${platformName}!`);
        if (userId) {
          await supabase
            .from('profiles')
            .update({
              health_sync_enabled: true,
              health_sync_weight: true,
              health_sync_workouts: true,
            })
            .eq('id', userId);
        }
      } else if (result.wasDenied) {
        localStorage.setItem('health_permanently_denied', 'true');
        toast('Please enable health access in Settings', { duration: 5000 });
      }
      
      localStorage.setItem(`health_prompted_${trigger}`, 'true');
      setDismissed(true);
      onClose();
    });
  }, [shouldShow, requestAuthorization, clearDeniedState, platformName, userId, trigger, onClose]);

  // Reset trigger flag when prompt closes
  useEffect(() => {
    if (!isOpen) {
      hasTriggered.current = false;
    }
  }, [isOpen]);

  // No custom UI - everything is handled by the native dialog
  return null;
}

// Hook to check if we should show the prompt
export function useHealthPrompt(userId: string | undefined, totalWorkouts: number) {
  const { isNativeApp, status } = useHealthKit();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showFirstWorkout, setShowFirstWorkout] = useState(false);

  useEffect(() => {
    if (!isNativeApp || status.isAuthorized) return;

    const onboardingPrompted = localStorage.getItem('health_prompted_onboarding');
    if (!onboardingPrompted) {
      const timer = setTimeout(() => setShowOnboarding(true), 2000);
      return () => clearTimeout(timer);
    }

    const firstWorkoutPrompted = localStorage.getItem('health_prompted_first_workout');
    if (!firstWorkoutPrompted && totalWorkouts === 1) {
      setShowFirstWorkout(true);
    }
  }, [isNativeApp, status.isAuthorized, totalWorkouts]);

  return {
    showOnboarding,
    setShowOnboarding,
    showFirstWorkout,
    setShowFirstWorkout,
  };
}
